import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton, Tooltip, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { CheckCircleOutline, Group, MoreVert, Replay, SwapHoriz } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import TransferTicketModal from "../TransferTicketModal";
import GroupParticipantsModal from "../GroupParticipantsModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: theme.spacing(0.5),
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: theme.spacing(0.5),
		minWidth: 0,
		whiteSpace: "nowrap",
		[theme.breakpoints.down("xs")]: {
			gap: 2,
			marginRight: theme.spacing(0.25),
		},
	},
	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		whiteSpace: "nowrap",
		minWidth: 0,
		"&:hover": {
			boxShadow: "none",
		},
		[theme.breakpoints.down("sm")]: {
			paddingLeft: theme.spacing(1),
			paddingRight: theme.spacing(1),
			"& .MuiButton-startIcon": {
				marginLeft: 0,
				marginRight: 0,
			},
			"& .ticket-action-label": {
				display: "none",
			},
		},
	},
	iconActionButton: {
		flexShrink: 0,
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1),
		},
	},
	hideOnXs: {
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	hideOnTight: {
		"@media (max-width: 380px)": {
			display: "none",
		},
	},
}));

const TicketActionButtons = ({
	ticket,
	showInternalEvents = false,
	onToggleInternalEvents = () => {},
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("xs"));
	const isTight = useMediaQuery("(max-width:380px)");
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [groupParticipantsModalOpen, setGroupParticipantsModalOpen] = useState(false);
	const [transferTicketPendingAction, setTransferTicketPendingAction] = useState("open");
	const [transferTicketAutoAssign, setTransferTicketAutoAssign] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		if (ticket.status === "pending" && status === "closed") {
			if (ticket?.queue?.allowManualAttendance === false) {
				// Queue without manual attendance can be resolved directly.
			} else {
				setTransferTicketPendingAction("closed");
				setTransferTicketAutoAssign(false);
				setTransferTicketModalOpen(true);
				return;
			}
		}

		if (
			ticket.status === "pending" &&
			status === "open" &&
			(ticket?.queue?.allowManualAttendance === false || !ticket?.queueId)
		) {
			setTransferTicketPendingAction("open");
			setTransferTicketAutoAssign(true);
			setTransferTicketModalOpen(true);
			return;
		}

		setLoading(true);
		try {
			const payload = {
				status,
			};

			if (userId !== undefined) {
				payload.userId = userId;
			}

			await api.put(`/tickets/${ticket.id}`, payload);

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`, { switchTab: "open" });
			} else {
				history.push("/tickets", {
					switchTab: ticket.status === "pending" ? "pending" : "open",
				});
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleCloseTransferTicketModal = () => {
		setTransferTicketModalOpen(false);
		setTransferTicketAutoAssign(false);
	};

	const handleOpenTransferModal = () => {
		setTransferTicketPendingAction("open");
		setTransferTicketAutoAssign(false);
		setTransferTicketModalOpen(true);
	};

	const menuActions = [];

	if (isXs) {
		menuActions.push({
			key: "participants",
			label: i18n.t("groupParticipantsModal.openButton"),
			onClick: () => setGroupParticipantsModalOpen(true),
		});
	}

	if (ticket.status === "closed" && isXs) {
		menuActions.push({
			key: "reopen",
			label: i18n.t("messagesList.header.buttons.reopen"),
			disabled: loading,
			onClick: e => handleUpdateTicketStatus(e, "open", user?.id),
		});
	}

	if (ticket.status === "open" && isXs) {
		menuActions.push({
			key: "return",
			label: i18n.t("messagesList.header.buttons.returnToPending"),
			disabled: loading,
			onClick: e =>
				handleUpdateTicketStatus(
					e,
					"pending",
					ticket?.user?.id || user?.id
				),
		});
	}

	if (ticket.status === "open" && isTight) {
		menuActions.push({
			key: "transfer",
			label: i18n.t("ticketOptionsMenu.transfer"),
			disabled: loading,
			onClick: handleOpenTransferModal,
		});
	}

	return (
		<div className={classes.actionButtons}>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
				pendingAction={transferTicketPendingAction}
				autoAssignLoggedUserAndQueue={transferTicketAutoAssign}
			/>
			<GroupParticipantsModal
				open={groupParticipantsModalOpen}
				onClose={() => setGroupParticipantsModalOpen(false)}
				ticket={ticket}
			/>
			<Tooltip
				title={i18n.t("groupParticipantsModal.openButton")}
			>
				<IconButton
					className={`${classes.iconActionButton} ${classes.hideOnXs}`}
					onClick={() => setGroupParticipantsModalOpen(true)}
					aria-label={i18n.t("groupParticipantsModal.openButton")}
				>
					<Group />
				</IconButton>
			</Tooltip>
			{ticket.status === "closed" && (
				<Tooltip title={i18n.t("messagesList.header.buttons.reopen")}>
					<span>
						<IconButton
							className={`${classes.iconActionButton} ${classes.hideOnXs}`}
							disabled={loading}
							onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
							aria-label={i18n.t("messagesList.header.buttons.reopen")}
						>
							<Replay />
						</IconButton>
					</span>
				</Tooltip>
			)}
			{ticket.status === "open" && (
				<>
					<Tooltip title={i18n.t("messagesList.header.buttons.returnToPending")}>
						<span>
							<IconButton
								className={`${classes.iconActionButton} ${classes.hideOnXs}`}
								disabled={loading}
								onClick={e =>
									handleUpdateTicketStatus(
										e,
										"pending",
										ticket?.user?.id || user?.id
									)
								}
								aria-label={i18n.t("messagesList.header.buttons.returnToPending")}
							>
								<Replay />
							</IconButton>
						</span>
					</Tooltip>
					{isXs ? (
						<>
							<Tooltip title={i18n.t("ticketOptionsMenu.transfer")}>
								<span className={classes.hideOnTight}>
									<IconButton
										className={classes.iconActionButton}
										color="primary"
										disabled={loading}
										onClick={handleOpenTransferModal}
										aria-label={i18n.t("ticketOptionsMenu.transfer")}
									>
										<SwapHoriz />
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
								<span>
									<IconButton
										className={classes.iconActionButton}
										color="primary"
										disabled={loading}
										onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
										aria-label={i18n.t("messagesList.header.buttons.resolve")}
									>
										<CheckCircleOutline />
									</IconButton>
								</span>
							</Tooltip>
						</>
					) : (
						<>
							<Button
								className={`${classes.actionButton} ${classes.hideOnTight}`}
								disabled={loading}
								size="small"
								variant="outlined"
								color="primary"
								startIcon={<SwapHoriz />}
								onClick={handleOpenTransferModal}
							>
								<span className="ticket-action-label">
									{i18n.t("ticketOptionsMenu.transfer")}
								</span>
							</Button>
							<ButtonWithSpinner
								className={classes.actionButton}
								loading={loading}
								size="small"
								variant="contained"
								color="primary"
								startIcon={<CheckCircleOutline />}
								onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
							>
								<span className="ticket-action-label">
									{i18n.t("messagesList.header.buttons.resolve")}
								</span>
							</ButtonWithSpinner>
						</>
					)}
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						showTransferOption={false}
						extraActions={menuActions}
						showInternalEvents={showInternalEvents}
						onToggleInternalEvents={onToggleInternalEvents}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<>
					<ButtonWithSpinner
						className={classes.actionButton}
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						className={classes.actionButton}
						loading={loading}
						size="small"
						variant="outlined"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						extraActions={menuActions}
						showInternalEvents={showInternalEvents}
						onToggleInternalEvents={onToggleInternalEvents}
					/>
				</>
			)}
		</div>
	);
};

export default TicketActionButtons;
